// Справка о направлении: подписи визы, безопасности, «практично», погоды — чистые функции (покрыто тестами).
// Тексты русские: переводчик интерфейса (shared/i18n) переводит их по словарю.
import type { AirLevel, AirQuality, DestinationInfo, ExtraItem, PracticalInfo, PrayerTimes, SafetyInfo, VisaInfo, WeatherSummary } from '@sayohat/shared';
import { plural } from '../../shared/lib/format';

export type Tone = 'good' | 'warn' | 'bad' | 'neutral';

/** «Без визы до 90 дней» / «Виза по прилёту» / «Электронная виза» / «Нужна виза» */
export function visaLabel(v: VisaInfo | null | undefined): string {
  if (!v) return '';
  switch (v.status) {
    case 'visa_free': return v.days ? `Без визы до ${v.days} ${plural(v.days, 'дня', 'дней', 'дней')}` : 'Без визы';
    case 'visa_on_arrival': return 'Виза по прилёту';
    case 'e_visa': return 'Электронная виза';
    case 'eta': return 'Электронное разрешение (ETA)';
    case 'visa_required': return 'Нужна виза';
    default: return '';
  }
}

export function visaTone(v: VisaInfo | null | undefined): Tone {
  if (!v) return 'neutral';
  return v.status === 'visa_free' ? 'good' : v.status === 'visa_required' ? 'bad' : 'warn';
}

/** Значок безопасности: только при уровне от 2 (Госдеп США) или предупреждении МИД Великобритании */
export function safetyBadge(s: SafetyInfo | null | undefined): { text: string; tone: Tone } | null {
  if (!s) return null;
  const level = s.level || 0;
  if (level >= 4 || s.alert === 'avoid_all_travel') return { text: 'Поездки не рекомендуются', tone: 'bad' };
  if (s.alert === 'avoid_all_but_essential_travel') return { text: 'Только необходимые поездки', tone: 'bad' };
  if (level === 3) return { text: 'Повышенный риск — взвесьте поездку', tone: 'warn' };
  if (s.alert) return { text: 'Есть районы, куда ехать не советуют', tone: 'warn' };
  if (level === 2) return { text: 'Будьте внимательнее обычного', tone: 'warn' };
  return null;
}

export const TIPPING: Record<PracticalInfo['tipping'], string> = {
  none: 'Чаевые не приняты',
  round: 'Чаевые: округлить счёт',
  included: 'Чаевые обычно включены в счёт',
  '5-10': 'Чаевые 5–10%',
  '10': 'Чаевые около 10%',
  '10-15': 'Чаевые 10–15%',
  '15-20': 'Чаевые 15–20%',
};

export const drivingLabel = (d: PracticalInfo['driving']): string => (d === 'left' ? 'Левостороннее движение' : 'Правостороннее движение');

/** Экстренные номера: [номер, кто] без повторов номера */
export function emergencyList(e: PracticalInfo['emergency']): [string, string][] {
  const names: [keyof PracticalInfo['emergency'], string][] = [['general', 'экстренная служба'], ['police', 'полиция'], ['ambulance', 'скорая помощь'], ['fire', 'пожарные']];
  const out: [string, string][] = [];
  for (const [k, name] of names) {
    const n = e[k];
    if (!n) continue;
    const same = out.find((x) => x[0] === n);
    if (!same) out.push([n, name]);
  }
  return out;
}

export const plugsLabel = (p: PracticalInfo): string => `${p.plugs.join(', ')} · ${p.voltage} В`;

const deg = (n: number) => `${n > 0 ? '+' : ''}${n}°`;

export function weatherLine(w: WeatherSummary): string {
  return `${deg(w.tMax)} днём, ${deg(w.tMin)} ночью`;
}

export const weatherShort = (w: WeatherSummary): string => `${deg(w.tMax)} / ${deg(w.tMin)}`;

/** Курс: «1 TRY ≈ 350 сум ($0,03)» */
export function rateLine(d: DestinationInfo): string {
  if (!d.currency || d.currency === 'UZS' || !d.uzsPerUnit) return '';
  const uzs = d.uzsPerUnit >= 100 ? Math.round(d.uzsPerUnit).toLocaleString('ru-RU') : d.uzsPerUnit.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  const usd = d.usdPerUnit && d.currency !== 'USD'
    ? ` (${d.usdPerUnit < 0.1 ? `${(1 / d.usdPerUnit).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ${d.currency} за $1` : `$${d.usdPerUnit.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}`})`
    : '';
  return `1 ${d.currency} ≈ ${uzs} сум${usd}`;
}

export function diffLine(d: DestinationInfo): string {
  if (d.diffHours === null || d.diffHours === undefined) return '';
  if (d.diffHours === 0) return 'время как в Ташкенте';
  return `${Math.abs(d.diffHours)} ч ${d.diffHours > 0 ? 'вперёд' : 'назад'} от Ташкента`;
}

/** Пункты «Полезного» и «ещё на …» по группам (у старого сервера group нет — считаем trip) */
export const extrasOfGroup = (items: ExtraItem[] | null | undefined, group: NonNullable<ExtraItem['group']>): ExtraItem[] =>
  (items || []).filter((x) => (x.group || 'trip') === group);

/** Есть ли пересадки у билета (для карточки бизнес-залов) */
export const hasTransfers = (t: { to: { transfers?: number | null }; back?: { transfers?: number | null } | null }): boolean =>
  (t.to.transfers || 0) > 0 || Boolean(t.back && (t.back.transfers || 0) > 0);

/** Даты для погоды: даты поездки или ближайшая неделя */
export function weatherDates(from: string, to: string, today: string): { from: string; to: string } {
  if (from && from >= today) return { from, to: to && to > from ? to : '' };
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 6);
  return { from: today, to: d.toISOString().slice(0, 10) };
}

// ---------- Намаз и аэропорт (открытые данные) ----------

/** Строки времени намаза по порядку: ключ, подпись (переводится), время */
export const PRAYER_ROWS: { key: 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha'; label: string }[] = [
  { key: 'fajr', label: 'Фаджр' }, { key: 'sunrise', label: 'Восход' }, { key: 'dhuhr', label: 'Зухр' },
  { key: 'asr', label: 'Аср' }, { key: 'maghrib', label: 'Магриб' }, { key: 'isha', label: 'Иша' },
];

/** Местные дата и время в часовом поясе: { day: YYYY-MM-DD, hm: HH:MM } */
export function localNow(tz: string | null | undefined, now = new Date()): { day: string; hm: string } | null {
  if (!tz) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
    const g = (t: string) => parts.find((p) => p.type === t)?.value || '';
    return { day: `${g('year')}-${g('month')}-${g('day')}`, hm: `${g('hour')}:${g('minute')}` };
  } catch {
    return null;
  }
}

/** Ближайший намаз (без восхода) сегодня по местному времени; null — расчёт не на сегодня или все прошли */
export function nextPrayer(p: PrayerTimes, now: { day: string; hm: string } | null): (typeof PRAYER_ROWS)[number] | null {
  if (!now || p.date !== now.day) return null;
  return PRAYER_ROWS.find((r) => r.key !== 'sunrise' && p[r.key] && (p[r.key] as string) > now.hm) || null;
}

/** «4,4 млн пассажиров в год» / «360 000 пассажиров в год» */
export function passengersLine(n: number | null | undefined): string {
  if (!n || n <= 0) return '';
  if (n >= 1e6) return `${(n / 1e6).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн пассажиров в год`;
  return `${(Math.round(n / 1000) * 1000).toLocaleString('ru-RU')} пассажиров в год`;
}

/** Воздух по шкале US AQI: подпись и тон */
export const AIR_LABELS: Record<AirLevel, { text: string; tone: Tone }> = {
  good: { text: 'Воздух чистый', tone: 'good' },
  moderate: { text: 'Воздух умеренный', tone: 'neutral' },
  sensitive: { text: 'Вредно для чувствительных', tone: 'warn' },
  unhealthy: { text: 'Вредный воздух', tone: 'bad' },
  very_unhealthy: { text: 'Очень вредный воздух', tone: 'bad' },
  hazardous: { text: 'Опасный воздух', tone: 'bad' },
};

/** Совет по воздуху: если сейчас или в ближайшие сутки хуже «умеренного» */
export function airAdvice(a: AirQuality | null | undefined): string {
  if (!a) return '';
  const worst = Math.max(a.aqi, a.max24h || 0);
  if (worst > 150) return 'Сократите время на улице, при астме возьмите лекарства и маску FFP2';
  if (worst > 100) return 'Детям, пожилым и людям с астмой лучше меньше гулять днём';
  return '';
}

/** Код страны → название на языке интерфейса (Intl), иначе код */
export function countryName(cc: string, lang: string): string {
  try { return new Intl.DisplayNames([lang === 'uz' ? 'uz' : lang === 'en' ? 'en' : 'ru'], { type: 'region' }).of(cc) || cc; } catch { return cc; }
}

/** Паспорта для выбора: сначала UZ и соседи, затем остальные по названию */
export function passportOptions(codes: string[], lang: string): { value: string; label: string }[] {
  const first = ['UZ', 'KZ', 'KG', 'TJ', 'TM', 'RU', 'TR', 'AZ'];
  const list = [...new Set(['UZ', ...codes])].map((cc) => ({ value: cc, label: countryName(cc, lang) }));
  const head = first.map((cc) => list.find((x) => x.value === cc)).filter((x): x is { value: string; label: string } => Boolean(x));
  const rest = list.filter((x) => !first.includes(x.value)).sort((a, b) => a.label.localeCompare(b.label, lang));
  return [...head, ...rest];
}
